/* eslint-disable @typescript-eslint/no-explicit-any */

import { CheckCircleIcon } from "icons";
import { FormEvent, useState } from "react";
import { Button as AriaButton } from "react-aria-components";
import AuthCode from "react-auth-code-input";
import PhoneInput from "react-phone-input-2";
import { Button, Form, Heading, Spinner, Text } from "ui-components";

import { makeToast } from "@/src/components/Toast/Toast";
import {
  TwoFactorDtoISOCode,
  UserResponseEntity,
  useUserDisableTwoFactor,
  useUserEnableTwoFactor,
  useUserVerifyTwoFactor,
} from "@/src/hooks/api/generated";
import { nullsToUndefined } from "@/src/utils/nullToUndefined";
import "react-phone-input-2/lib/style.css";

interface IUpdatePasswordFormProps {
  userData: UserResponseEntity;
  refetch: () => void;
}

export function UpdateTwoFactorForm({
  userData,
  refetch,
}: IUpdatePasswordFormProps): JSX.Element {
  const { mutate: enableTwoFactor, status: enableStatus } =
    useUserEnableTwoFactor();
  const { mutate: verifyTwoFactor, status: verifyStatus } =
    useUserVerifyTwoFactor();
  const { mutate: disableTwoFactor, status: disableStatus } =
    useUserDisableTwoFactor();

  const [isTwoFactorModalVisible, setIsTwoFactorModalVisible] = useState(false);

  const [formState, setFormState] = useState({
    phoneNumber: "",
    ISOCode: "gb",
    phoneCountryCode: "44",
    code: "",
    error: "Required.",
    touched: false,
  });

  const onSubmitTwoFactor = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    if (
      !formState.ISOCode ||
      !formState.phoneNumber ||
      !formState.phoneCountryCode
    ) {
      makeToast({
        title: "Please enter a valid phone number to proceed.",
        intent: "warning",
      });
    }

    formState.phoneNumber = formState.phoneNumber.startsWith(
      formState.phoneCountryCode
    )
      ? formState.phoneNumber.slice(formState.phoneCountryCode.length)
      : formState.phoneNumber;

    enableTwoFactor(
      {
        data: {
          ISOCode: formState.ISOCode.toUpperCase() as TwoFactorDtoISOCode,
          phoneCountryCode: `+${formState.phoneCountryCode}`,
          phoneNumber: formState.phoneNumber,
        },
      },
      {
        onSuccess: (data) => {
          if (!data.success) {
            makeToast({
              title: data.message,
              intent: "warning",
            });
          } else {
            setIsTwoFactorModalVisible(true);
          }
        },
        onError: () => {
          makeToast({
            title: "Something went wrong.",
            intent: "danger",
          });
        },
      }
    );
  };

  const handleVerifySubmit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();

    formState.phoneNumber = formState.phoneNumber.startsWith(
      formState.phoneCountryCode
    )
      ? formState.phoneNumber.slice(formState.phoneCountryCode.length)
      : formState.phoneNumber;

    verifyTwoFactor(
      {
        data: {
          ISOCode: formState.ISOCode.toUpperCase() as TwoFactorDtoISOCode,
          phoneCountryCode: `+${formState.phoneCountryCode}`,
          phoneNumber: formState.phoneNumber,
          code: formState.code,
        },
      },
      {
        onSuccess: (data) => {
          if (data.success) {
            makeToast({
              title: data.message,
              intent: "success",
            });
            refetch();
            setIsTwoFactorModalVisible(false);
          } else {
            setFormState({
              ...formState,
              error: data.message,
            });
            makeToast({
              title: data.message,
              intent: "danger",
            });
          }
        },
        onError: () => {
          makeToast({
            title: "Something went wrong.",
            intent: "danger",
          });
        },
      }
    );
  };

  function handleDisableTwoFactor(): void {
    disableTwoFactor(undefined, {
      onSuccess: (data) => {
        makeToast({
          title: data.message,
          intent: "success",
        });
        refetch();
      },
      onError: () => {
        makeToast({
          title: "Something went wrong.",
          intent: "danger",
        });
      },
    });
  }

  return (
    <div className="flex flex-col items-center gap-y-5">
      <Heading variant="h3">
        Secure your account with a two-factor authentication
      </Heading>
      {!nullsToUndefined(userData)?.userTaxonomy.twoFactorPhoneNumber && (
        <div className="w-[597px] text-center">
          <Text>
            Two-Factor Authentication (2FA) gives you an additional layer of
            security by requiring a verification code sent to your phone number
            before you can sign in.
          </Text>
        </div>
      )}
      {!isTwoFactorModalVisible ? (
        <>
          {!nullsToUndefined(userData)?.userTaxonomy.twoFactorPhoneNumber ? (
            <Form
              className="flex flex-col gap-y-5 w-full max-w-[500px] items-center"
              onSubmit={onSubmitTwoFactor}
            >
              <PhoneInput
                containerClass="w-full mx-auto"
                inputClass="!w-full mx-auto"
                country={formState.ISOCode}
                enableSearch
                value={formState.phoneNumber}
                onChange={(phone, country: any) => {
                  const isExplicitCountryChange =
                    formState.phoneCountryCode !== country.dialCode;
                  setFormState((prevState) => ({
                    ...prevState,
                    phoneNumber: phone,
                    ISOCode: isExplicitCountryChange
                      ? country.countryCode
                      : prevState.ISOCode,
                    phoneCountryCode: country.dialCode,
                  }));
                }}
              />
              <Button
                type="submit"
                isLoading={enableStatus === "pending"}
                isDisabled={enableStatus === "pending"}
                className="mt-5"
              >
                <Text>Continue</Text>
              </Button>
            </Form>
          ) : (
            <div className="flex flex-col items-center gap-5 mt-5">
              <div className="w-[239px] rounded-[20px] p-5 bg-success bg-opacity-20 flex flex-col text-center gap-5">
                <CheckCircleIcon className="w-[54px] h-[54px] text-success mx-auto" />
                <Text align="center">
                  The two-factor authentication is enabled on your account
                </Text>
              </div>
              <div className="flex space-x-2">
                <AriaButton
                  onPress={() => handleDisableTwoFactor()}
                  className="underline"
                >
                  <Text>Deactivate the two-factor authentication</Text>
                </AriaButton>
                {disableStatus === "pending" && (
                  <Spinner size="md" intent="brand-primary" />
                )}
              </div>
            </div>
          )}
        </>
      ) : (
        <Form
          onSubmit={handleVerifySubmit}
          className="max-w-[500px] flex flex-col gap-y-5"
        >
          <AuthCode
            autoFocus
            allowedCharacters="numeric"
            onChange={(res: string) => {
              setFormState({
                ...formState,
                code: res,
                touched: true,
                error: res ? "" : "Required.",
              });
            }}
            containerClassName="flex justify-between"
            inputClassName="w-[15%] rounded-main outline-none p-2 text-sm font-medium border border-background-dark hover:border-primary focus-within:border-primary relative h-10 text-center"
          />
          <Button
            type="submit"
            isDisabled={!!formState.error || verifyStatus === "pending"}
            isLoading={verifyStatus === "pending"}
          >
            Confirm the code
          </Button>
        </Form>
      )}
    </div>
  );
}
